'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import 'flag-icons/css/flag-icons.min.css'
import { languages } from '../data'
import { AppLocale, setAppLocale } from '@/i18n'
import styles from './Languages.module.css'

type LanguageSwitcherProps = {
  variant?: 'topbar' | 'login'
  toggleClassName?: string
}

function FlagIcon({ countryCode, className }: { countryCode: string; className?: string }) {
  return <span className={clsx('fi', `fi-${countryCode}`, className)} aria-hidden />
}

const LanguageSwitcher = ({ variant = 'topbar', toggleClassName }: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const activeCode = i18n.language.split('-')[0]

  const activeLanguage = useMemo(
    () => languages.find((item) => item.code === activeCode) ?? languages[0],
    [activeCode]
  )

  const filteredLanguages = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return languages
    return languages.filter(
      (item) =>
        item.code.includes(q) ||
        item.name.toLowerCase().includes(q) ||
        item.nativeName.toLowerCase().includes(q)
    )
  }, [query])

  const handleSelect = (code: string) => {
    void setAppLocale(code as AppLocale)
    setIsOpen(false)
    setQuery('')
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setQuery('')
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        setQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus()
    }
  }, [isOpen])

  const toggleClass = clsx(
    variant === 'login' ? styles.toggleLogin : styles.toggle,
    toggleClassName,
    isOpen && (variant === 'login' ? styles.toggleLoginOpen : styles.toggleOpen)
  )

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={toggleClass}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('topbar.changeLanguage', { defaultValue: 'Change language' })}
      >
        <FlagIcon countryCode={activeLanguage.flagCountry} className={styles.flag} />
      </button>

      {isOpen && (
        <div className={styles.panel} role="listbox" aria-label={t('topbar.languages', { defaultValue: 'Languages' })}>
          <div className={styles.searchWrap}>
            <input
              ref={searchRef}
              type="search"
              className={styles.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('topbar.searchLanguages', { defaultValue: 'Search languages…' })}
              aria-label={t('topbar.searchLanguages', { defaultValue: 'Search languages…' })}
            />
          </div>
          <div className={styles.list} data-scroll-menu>
            {filteredLanguages.length === 0 ? (
              <p className={styles.empty}>{t('topbar.noLanguagesFound', { defaultValue: 'No languages found' })}</p>
            ) : (
              filteredLanguages.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  role="option"
                  aria-selected={item.code === activeCode}
                  className={clsx(styles.item, item.code === activeCode && styles.itemActive)}
                  onClick={() => handleSelect(item.code)}
                >
                  <FlagIcon countryCode={item.flagCountry} className={styles.itemFlag} />
                  <span className={styles.itemLabel}>
                    <span className={styles.itemNative}>{item.nativeName}</span>
                    <span className={styles.itemEnglish}>{item.name}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
